import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight, Instagram, Facebook, Globe, MessageCircle } from "lucide-react";
import QuoteForm from "./QuoteForm";
import { WhatsAppPill } from "./WhatsAppButton";
import { BIRDS, LOGO } from "@/lib/brand";
import { useLang } from "@/lib/i18n";

const EMAILS = [
  { key: "General", value: "arvindprinter@gmail.com" },
  { key: "Director", value: "director@arvindprinters.com" },
  { key: "Admin", value: "admin@arvindprinters.com" },
  { key: "Accounts", value: "accounts@arvindprinters.com" },
];

const PHONES = [
  { key: "Director · Arvind Mali", value: "+91 8806 004 000" },
  { key: "Operations · Ajay Linge", value: "+91 8806 003 999" },
  { key: "Operations · Abhishek Linge", value: "+91 8554 286 000" },
];

const LEADERSHIP = [
  { name: "Arvind Mali", title: "Managing Director" },
  { name: "Ajay Linge", title: "Director of Operations" },
  { name: "Abhishek Linge", title: "Director of Operations" },
];

const SOCIALS = [
  {
    key: "WhatsApp",
    Icon: MessageCircle,
    handle: "+91 83900 63999",
    href: "https://wa.me/918390063999?text=Hi%20Arvind%20Printers%2C%20I%27d%20like%20to%20request%20a%20quote.",
  },
  {
    key: "Instagram",
    Icon: Instagram,
    handle: "@arvindprinters",
    href: "https://www.instagram.com/arvindprinters?igsh=MWE4cTJsMTN5MWNsNQ%3D%3D&utm_source=qr",
  },
  {
    key: "Facebook",
    Icon: Facebook,
    handle: "Arvind Printers",
    href: "https://www.facebook.com/share/1c8tNxkMEm/?mibextid=wwXIfr",
  },
  {
    key: "IndiaMart",
    Icon: Globe,
    handle: "arvindprinters",
    href: "https://www.indiamart.com/arvindprinters",
  },
];

const MAP_EMBED =
  "https://www.google.com/maps?q=Chikalthana+MIDC+Chhatrapati+Sambhajinagar&output=embed";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="relative bg-[#0A0A0A] text-[#F4F4F0] border-t border-black"
    >
      {/* CTA header */}
      <div className="relative border-b border-white/15">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-8">
              <div className="flex items-center gap-4 mb-8 text-[#F4F4F0]/70">
                <span className="w-10 h-px bg-[#F4F4F0]/60" />
                <span className="font-mono text-[10px] tracking-[0.35em] uppercase">
                  {t("footer.chapter")}
                </span>
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="font-display text-[13vw] md:text-[8.4vw] leading-[0.9] tracking-tight"
              >
                {t("footer.big_title_1")}{" "}
                <em className="italic">{t("footer.big_title_italic")}</em>{" "}
                {t("footer.big_title_2")}
              </motion.h2>
            </div>
            <div className="md:col-span-4 flex flex-col items-start md:items-end justify-end gap-6">
              <div className="bg-[#F4F4F0] border border-[#F4F4F0]/40 p-4 md:p-6 inline-flex flex-col items-center">
                <img
                  src={LOGO.stacked_tagline}
                  alt="Arvind Printers — Design | Brand | Print"
                  className="w-40 md:w-52 object-contain"
                  data-testid="footer-logo-stacked"
                />
              </div>
              <img
                src={LOGO.badge_32}
                alt="32 Years of Printing Excellence"
                className="w-24 md:w-28 opacity-95"
                data-testid="footer-badge-32"
              />
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60 md:text-right">
                {t("footer.tagline_line1")} <br />
                {t("footer.tagline_line2")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <QuoteForm />
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Address */}
          <div className="md:col-span-2 border-t border-white/20 pt-4">
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="w-4 h-4 opacity-80" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
                {t("footer.the_facility")}
              </span>
            </div>
            <p
              data-testid="footer-address"
              className="font-display text-2xl md:text-3xl leading-snug"
            >
              Plot No. 10, E-20, Chhatrapati Udyog Society, Millennium Park
              Road, Chikalthana MIDC, Chhatrapati Sambhajinagar — 431 006, M.S.,
              India.
            </p>
          </div>

          {/* Emails */}
          <div className="border-t border-white/20 pt-4">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-4 h-4 opacity-80" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
                {t("footer.write")}
              </span>
            </div>
            <ul className="space-y-3">
              {EMAILS.map((e) => (
                <li key={e.value}>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/50">
                    {e.key}
                  </div>
                  <a
                    href={`mailto:${e.value}`}
                    data-testid={`footer-email-${e.key.toLowerCase()}`}
                    className="font-mono text-[13px] tracking-wide hover:text-[color:var(--ap-yellow)] transition-colors break-all"
                  >
                    {e.value}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Phones */}
          <div className="border-t border-white/20 pt-4">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="w-4 h-4 opacity-80" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
                {t("footer.call")}
              </span>
            </div>
            <ul className="space-y-3">
              {PHONES.map((p) => (
                <li key={p.value}>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/50">
                    {p.key}
                  </div>
                  <a
                    href={`tel:${p.value.replace(/\s+/g, "")}`}
                    data-testid={`footer-phone-${p.value.replace(/\s+/g, "")}`}
                    className="font-mono text-[13px] tracking-wide hover:text-[color:var(--ap-cyan)] transition-colors"
                  >
                    {p.value}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Leadership */}
          <div className="md:col-span-2 border-t border-white/20 pt-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60 mb-4">
              {t("footer.leadership")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {LEADERSHIP.map((l) => (
                <div key={l.name} data-testid={`footer-leader-${l.name.split(" ")[0].toLowerCase()}`}>
                  <div className="font-display text-xl md:text-2xl leading-tight">
                    {l.name}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F4F4F0]/60 mt-1">
                    {l.title}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social */}
          <div className="md:col-span-2 border-t border-white/20 pt-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60 mb-4">
              Follow the press
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SOCIALS.map(({ key, Icon, handle, href }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${key.toLowerCase()}`}
                  className="group border border-white/20 hover:border-white p-4 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4" />
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
                        {key}
                      </div>
                      <div className="font-mono text-[12px] tracking-wide">
                        {handle}
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 pb-20 md:pb-28">
        <div className="border-t border-white/20 pt-6 mb-6 flex items-center justify-between">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
            {t("footer.locate")}
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Chikalthana+MIDC+Chhatrapati+Sambhajinagar"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-map-open"
            className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2 hover:text-[color:var(--ap-yellow)] transition-colors"
          >
            {t("footer.open_maps")} <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="relative aspect-[16/9] md:aspect-[21/9] border border-white/30 overflow-hidden bg-black">
          <iframe
            title="Arvind Printers — Chikalthana MIDC"
            src={MAP_EMBED}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="absolute inset-0 w-full h-full grayscale contrast-125 opacity-90"
            allowFullScreen
          />
          <div className="pointer-events-none absolute inset-0 mix-blend-color bg-[color:var(--ap-cyan)]/10" />
        </div>
      </div>

      {/* Signature bar */}
      <div className="border-t border-white/15">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img
              src={LOGO.mark}
              alt="Arvind Printers"
              className="w-9 h-9 object-contain"
            />
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
              © {new Date().getFullYear()} Arvind Printers · {t("footer.rights")}
            </div>
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60 flex items-center gap-4">
            <span>Design | Brand | Print</span>
            <span className="text-[#F4F4F0]/30">·</span>
            <span>Chikalthana MIDC · 431 006</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
